/* eslint-disable  */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { Plus, ClipboardCheck, ArrowLeft, Lock } from "lucide-react";
import { inventoryActionCreators } from "../../services/Actions";
import RenderInventoryPage from "../RenderInventoryPage";
import Loader from "../../Components/Loader";
import ProductModal from "../../HirePurchaseModel/Modal/AddProductModal";

function StockTake() {
  const dispatch = useDispatch();
  const {
    GetStockTakes,
    GetStockTakeById,
    OpenStockTake,
    RecordCount,
    CloseStockTake,
    ResetInventoryResponse,
  } = bindActionCreators(inventoryActionCreators, dispatch);

  const inventory = useSelector((state) => state.inventory);
  const { stock_takes, stock_take_detailed, response, isLoading } = inventory;

  const [openNew, setOpenNew] = useState(false);
  const [newForm, setNewForm] = useState({ location: "", note: "" });
  const [activeId, setActiveId] = useState(null);
  const [countBarcode, setCountBarcode] = useState("");
  const [countQty, setCountQty] = useState(1);

  useEffect(() => {
    GetStockTakes();
  }, []);

  useEffect(() => {
    if (activeId) GetStockTakeById(activeId);
  }, [activeId]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setOpenNew(false);
      setNewForm({ location: "", note: "" });
      setTimeout(() => {
        ResetInventoryResponse();
        GetStockTakes();
        if (activeId) GetStockTakeById(activeId);
      }, 1000);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetInventoryResponse(), 1500);
    }
  }, [response]);

  const submitCount = (e) => {
    e.preventDefault();
    if (!countBarcode.trim()) return;
    RecordCount(activeId, { barcode: countBarcode.trim(), counted_qty: Number(countQty) || 1 });
    setCountBarcode("");
  };

  const StatusPill = ({ status }) => (
    <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );

  // ── Detail view ──
  if (activeId && stock_take_detailed?._id === activeId) {
    const s = stock_take_detailed;
    return (
      <RenderInventoryPage title={"Stock Take Session"}>
        {isLoading && <Loader />}
        <button onClick={() => setActiveId(null)} className="flex items-center gap-2 text-sm text-gray-600 mb-4 hover:text-emerald-700">
          <ArrowLeft size={16} /> Back to sessions
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <StatusPill status={s.status} />
            <span className="text-sm text-gray-500">{s.location || "No location"}</span>
          </div>
          {s.status === "open" && (
            <button
              onClick={() => CloseStockTake(activeId)}
              className="flex items-center gap-2 text-white bg-rose-600 hover:bg-rose-700 font-medium rounded-lg text-sm px-5 py-2.5 shadow"
            >
              <Lock size={16} /> Close & Post Variances
            </button>
          )}
        </div>

        {s.status === "open" && (
          <form onSubmit={submitCount} className="flex items-center gap-3 mb-5 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100">
            <input
              autoFocus
              value={countBarcode}
              onChange={(e) => setCountBarcode(e.target.value)}
              placeholder="Scan / type barcode to count"
              className="flex-1 px-3 py-2.5 font-mono border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
            />
            <input type="number" min={1} value={countQty} onChange={(e) => setCountQty(e.target.value)} className="w-24 px-3 py-2.5 text-center border border-gray-300 rounded-lg" />
            <button type="submit" className="px-5 py-2.5 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold">Count</button>
          </form>
        )}

        <section className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">System</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Counted</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(s.lines || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">No items counted yet.</td></tr>
                ) : (
                  s.lines.map((l, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-3 text-sm font-mono">{l?.item?.sku || l?.item?._id || "item"}</td>
                      <td className="px-4 py-3 text-center text-sm">{l.system_qty}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold">{l.counted_qty}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-bold ${l.variance === 0 ? "text-gray-400" : l.variance > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {l.variance > 0 ? `+${l.variance}` : l.variance}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        <ToastContainer autoClose={2000} />
      </RenderInventoryPage>
    );
  }

  // ── List view ──
  return (
    <RenderInventoryPage title={"Stock Take"}>
      {isLoading && <Loader />}
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-gray-500">Open a session, scan items in the Count mode, then close to post variances.</p>
        <button onClick={() => setOpenNew(true)} className="flex items-center gap-2 text-white bg-emerald-600 hover:bg-emerald-700 font-medium rounded-lg text-sm px-5 py-2.5 shadow">
          <Plus size={16} /> New Session
        </button>
      </div>

      <section className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Lines</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase">Variance</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(stock_takes || []).length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">No stock-take sessions yet.</td></tr>
              ) : (
                stock_takes.map((s) => (
                  <tr key={s._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-6 py-4 text-sm font-semibold">{s.location || "—"}</td>
                    <td className="px-4 py-4 text-center"><StatusPill status={s.status} /></td>
                    <td className="px-4 py-4 text-center text-sm">{s.lines?.length || 0}</td>
                    <td className="px-4 py-4 text-center text-sm font-semibold">{s.total_variance ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => setActiveId(s._id)} className="flex items-center gap-1 mx-auto text-emerald-600 hover:text-emerald-800 text-sm font-semibold">
                        <ClipboardCheck size={16} /> Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {openNew && (
        <ProductModal closeModal={setOpenNew} width="40%" title={"New Stock-Take Session"}>
          <form onSubmit={(e) => { e.preventDefault(); OpenStockTake(newForm); }} className="p-4">
            <div className="mb-4">
              <label className="block mb-2 text-sm font-bold text-gray-700">Location / Area</label>
              <input value={newForm.location} onChange={(e) => setNewForm({ ...newForm, location: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5" placeholder="e.g. Main store, Shelf A" />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-bold text-gray-700">Note</label>
              <textarea rows="2" value={newForm.note} onChange={(e) => setNewForm({ ...newForm, note: e.target.value })} className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5" />
            </div>
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <button type="button" onClick={() => setOpenNew(false)} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button type="submit" disabled={isLoading} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 shadow">{isLoading ? "Opening…" : "Open Session"}</button>
            </div>
          </form>
        </ProductModal>
      )}

      <ToastContainer autoClose={2000} />
    </RenderInventoryPage>
  );
}

export default StockTake;
